import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import RIcon from "../../../components/RIcon";

const NAV_ITEMS = [
  { label: "首页", targetId: "hero" },
  { label: "功能介绍", targetId: "features" },
  { label: "关于系统", targetId: "about" },
  { label: "使用手册", targetId: "manual", isLink: true, href: "/manual" },
];

export default function HomeNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);

      // Update active section based on scroll position
      const sections = NAV_ITEMS.map((n) => document.getElementById(n.targetId));
      const scrollMid = window.scrollY + window.innerHeight / 3;
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = sections[i];
        if (el && el.offsetTop <= scrollMid) {
          setActiveSection(NAV_ITEMS[i].targetId);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (targetId: string) => {
    setMobileOpen(false);
    if (targetId === "hero") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.getElementById(targetId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-sm border-b border-gray-100" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <button onClick={() => scrollTo("hero")} className="flex items-center gap-3 cursor-pointer">
          <img
            src="https://public.readdy.ai/ai/img_res/a84828b1-10f3-4f8c-851b-b2f4fe56a73c.png"
            alt="协和医疗"
            className="h-8 w-auto"
          />
          <span
            className={`font-semibold text-base whitespace-nowrap ${
              scrolled ? "text-[#1A2B3C]" : "text-white"
            }`}
          >
            协和医疗全域AI系统
          </span>
        </button>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-10">
          {NAV_ITEMS.map((item) => {
            const isActive = activeSection === item.targetId;
            if (item.isLink && item.href) {
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`relative text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    scrolled
                      ? "text-[#4A5568] hover:text-[#0EA5A0]"
                      : "text-white/70 hover:text-white"
                  }`}
                >
                  {item.label}
                </Link>
              );
            }
            return (
              <button
                key={item.label}
                onClick={() => scrollTo(item.targetId)}
                className={`relative text-sm font-medium transition-colors cursor-pointer ${
                  scrolled
                    ? isActive
                      ? "text-[#0EA5A0]"
                      : "text-[#4A5568] hover:text-[#0EA5A0]"
                    : isActive
                    ? "text-white"
                    : "text-white/70 hover:text-white"
                }`}
              >
                {item.label}
                {/* Active underline */}
                <span
                  className={`absolute -bottom-1 left-0 right-0 h-0.5 rounded-full bg-[#0EA5A0] transition-all duration-300 ${
                    isActive ? "opacity-100 scale-x-100" : "opacity-0 scale-x-0"
                  }`}
                />
              </button>
            );
          })}
        </nav>

        {/* Right Actions */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/register"
            className={`px-5 h-9 flex items-center justify-center rounded-full text-sm font-medium border transition-all whitespace-nowrap ${
              scrolled
                ? "border-[#0EA5A0] text-[#0EA5A0] hover:bg-[#0EA5A0]/10"
                : "border-white/60 text-white hover:border-white"
            }`}
          >
            注册
          </Link>
          <Link
            to="/login"
            className="px-5 h-9 flex items-center justify-center rounded-full text-sm font-medium bg-[#0EA5A0] text-white hover:bg-[#0D9590] transition-all whitespace-nowrap"
          >
            登录
          </Link>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="md:hidden w-8 h-8 flex items-center justify-center cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <RIcon name="close-line" className={`${mobileOpen ? "" : ""} text-xl ${ scrolled ? "text-[#1A2B3C]" : "text-white" }`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4">
          <nav className="flex flex-col gap-1 mb-4">
            {NAV_ITEMS.map((item) =>
              item.isLink && item.href ? (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-left text-sm py-2.5 px-3 rounded-lg text-[#4A5568] hover:text-[#0EA5A0] hover:bg-gray-50"
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.label}
                  onClick={() => scrollTo(item.targetId)}
                  className={`text-left text-sm py-2.5 px-3 rounded-lg transition-colors cursor-pointer ${
                    activeSection === item.targetId
                      ? "text-[#0EA5A0] bg-[#0EA5A0]/8 font-medium"
                      : "text-[#4A5568] hover:text-[#0EA5A0] hover:bg-gray-50"
                  }`}
                >
                  {item.label}
                </button>
              )
            )}
          </nav>
          <div className="flex gap-3">
            <Link
              to="/register"
              className="flex-1 h-9 flex items-center justify-center rounded-full text-sm border border-[#0EA5A0] text-[#0EA5A0] whitespace-nowrap"
            >
              注册
            </Link>
            <Link
              to="/login"
              className="flex-1 h-9 flex items-center justify-center rounded-full text-sm bg-[#0EA5A0] text-white whitespace-nowrap"
            >
              登录
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
